import { useState, useEffect } from "react";
import { NavLink, Link, useLocation, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Globe, LogIn, LogOut } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { useAuth, login } from "@/context/AuthContext";
import Logo from "@/components/Logo";

const links = [
  { to: "/projet", key: "nav.about" },
  { to: "/technique", key: "nav.technical" },
  { to: "/impact", key: "nav.esg" },
  { to: "/suivi", key: "nav.monitoring" },
  { to: "/contact", key: "nav.contact" },
];

export default function Navbar() {
  const { t, lang, toggle } = useLang();
  const { user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isHome = pathname === "/";
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  const solid = scrolled || !isHome;

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-300 ${
        solid ? "bg-white/80 backdrop-blur-xl border-b border-slate-200" : "bg-transparent"
      }`}
      data-testid="navbar"
    >
      <nav className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <Logo dark={!solid} />
        <div className="hidden lg:flex items-center gap-7">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.to.replace("/", "")}`}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors duration-200 ${
                  solid
                    ? isActive ? "text-sky-700" : "text-slate-700 hover:text-sky-700"
                    : isActive ? "text-white" : "text-white/80 hover:text-white"
                }`
              }
            >
              {t(l.key)}
            </NavLink>
          ))}
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={toggle}
            data-testid="lang-toggle"
            className={`flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 ${
              solid ? "text-slate-700 hover:text-sky-700" : "text-white/90 hover:text-white"
            }`}
          >
            <Globe className="w-4 h-4" />
            <span className="mono uppercase">{lang}</span>
          </button>
          {user ? (
            <div className="flex items-center gap-2">
              <Link
                to="/espace"
                data-testid="nav-espace"
                className="flex items-center gap-2 rounded-full bg-sky-800 text-white text-sm font-medium pl-1.5 pr-4 py-1.5 hover:bg-sky-700 active:scale-95 transition-[background-color,transform] duration-200"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} referrerPolicy="no-referrer" className="w-6 h-6 rounded-full object-cover" />
                ) : (
                  <span className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center text-xs">{user.name?.[0] || "?"}</span>
                )}
                <span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
              </Link>
              <button
                onClick={onLogout}
                data-testid="nav-logout"
                aria-label={t("nav.logout")}
                className={`p-2 rounded-full transition-colors duration-200 ${solid ? "text-slate-600 hover:text-sky-700 hover:bg-slate-100" : "text-white/80 hover:text-white hover:bg-white/10"}`}
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          ) : (
            <button
              onClick={login}
              data-testid="nav-login"
              className="inline-flex items-center gap-2 rounded-full bg-sky-800 text-white text-sm font-medium px-5 py-2.5 hover:bg-sky-700 active:scale-95 transition-[background-color,transform] duration-200"
            >
              <LogIn className="w-4 h-4" />
              <span className="hidden sm:inline">{t("nav.login")}</span>
            </button>
          )}
          <button
            className={`lg:hidden ${solid ? "text-slate-900" : "text-white"}`}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X /> : <Menu />}
          </button>
        </div>
      </nav>
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="lg:hidden overflow-hidden bg-white border-b border-slate-200"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-nav-${l.to.replace("/", "")}`}
                  className="text-left py-3 text-base text-slate-800 border-b border-slate-100 last:border-0"
                >
                  {t(l.key)}
                </NavLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
